import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ProfileItem from './ProfileItem';
import i18n from 'i18n';
import Clock from 'components/Clock';
import { SupportInfo } from 'components/SupportInfo';
import withKeepingLastFocus, { LastFocusKeeper } from 'components/HOCs/withKeepingLastFocus';
import { StyledProfileButtonsWrapper, StyledProfileTitle } from 'components/Profile/StyledComponents';
import { PROFILE_ITEM_MARGIN, PROFILE_ITEM_WIDTH } from 'components/Profile/ProfileItem';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import useModals from 'hooks/useModals';
import { useSelector } from 'react-redux';
import { getIsLifecellFlag } from 'reducers/profile/selectors';

const LAST_FOCUS_KEY = 'PROFILE';
const SECTION_NAME = 'PROFILE_ITEMS_LIST';

const computeOffset = (focusedItemIndex, size) => {
  const listOffset = 4;
  const itemWidth = PROFILE_ITEM_WIDTH + PROFILE_ITEM_MARGIN;

  if (listOffset === 0) return focusedItemIndex * itemWidth;

  if (size - focusedItemIndex < listOffset) {
    return (size - listOffset) * itemWidth;
  }

  if (focusedItemIndex > 1) {
    return (focusedItemIndex - 1) * itemWidth;
  }

  return 0;
};

const Profile = ({ items, data, history, match, isLoggedIn, fetchBalance, lastFocusKeeper }) => {
  const isLifecellUser = useSelector(getIsLifecellFlag);
  const [focusedItemIndex, setFocusedItemIndex] = useState(0);
  const { showLoginModal, showLogoutModal, showLifecellPaymentModal } = useModals();

  const onEnter = (item, itemId) => {
    const focusBackConfig = { sectionToFocus: SECTION_NAME };

    switch (item.id) {
      case 'authorization':
        if (isLoggedIn) {
          showLogoutModal(focusBackConfig);
        } else {
          innerAnalyticsVolia.postEvent({ eventGroup: 'profile', eventName: 'click sign in' });

          history.push(`${match.url}/${item.path}`);
        }
        break;
      case 'promocode': {
        innerAnalyticsVolia.postEvent({ eventGroup: 'profile', eventName: 'click promo code' });

        if (isLoggedIn) {
          history.push(`${match.url}/${item.path}`);
        } else {
          const search = '?onSuccess=/profile/promocode';
          showLoginModal(focusBackConfig, search, i18n.t('common.modal.loginModalPromocodeTitle'));
        }
        break;
      }
      case 'user-packages':
        innerAnalyticsVolia.postEvent({ eventGroup: 'subscription management', eventName: 'click all packages' });
        if (isLifecellUser) {
          showLifecellPaymentModal(null, focusBackConfig);
        } else {
          history.push(`${match.url}/${item.path}`);
        }
        break;
      default:
        history.push(`${match.url}/${item.path}`);
    }
    lastFocusKeeper.save(LAST_FOCUS_KEY, `#${itemId}`);
  };

  useEffect(() => {
    if (isLoggedIn) fetchBalance();

    const lastFocus = lastFocusKeeper.select(LAST_FOCUS_KEY);

    if (lastFocus.hasSaved()) {
      lastFocus.apply(lastFocus.reset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Clock />
      <StyledProfileTitle>{i18n.t('profile.title')}</StyledProfileTitle>
      <StyledProfileButtonsWrapper
        sectionName={SECTION_NAME}
        config={{ enterTo: 'last-focused' }}
        defaultSection
        offset={computeOffset(focusedItemIndex, items.length)}
      >
        {items.map((item, index) => {
          const itemId = `profile-item-${index}`;
          return (
            <ProfileItem
              onEnter={() => onEnter(item, itemId)}
              onFocus={(i) => setFocusedItemIndex(i)}
              itemIndex={index}
              item={item}
              id={itemId}
              key={item.title}
            >
              {item.title}
            </ProfileItem>
          );
        })}
      </StyledProfileButtonsWrapper>
      <SupportInfo />
    </>
  );
};

Profile.propTypes = {
  fetchBalance: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  items: PropTypes.array.isRequired,
  lastFocusKeeper: PropTypes.instanceOf(LastFocusKeeper).isRequired,
};

export default withKeepingLastFocus(Profile);
